/**
 * Next-best-action cards. Written as if AI-generated (badge + evidence-led copy);
 * in production the same cards are produced by the backend Claude model over the
 * real mastery + misconception facts.
 */

import { Sparkles, ArrowRight, Layers, Flag } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { NextAction } from './mockData';
import { topicLabel } from './mockData';
import { SEVERITY_META, KIND_LABEL, Pill } from './charts';

export function NextBestActions({ actions, title, sub, scope, columns = 1, onOpenTopic }: {
  actions: NextAction[];
  title: string;
  sub?: string;
  scope: 'class' | 'student';
  columns?: 1 | 2;
  onOpenTopic?: (topicId: string) => void;
}) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#7C3AED]/30 bg-[#7C3AED]/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#6D28D9] dark:text-[#B69BF5]">
            <Sparkles className="h-3 w-3" /> AI suggestions
          </div>
          <h3 className="font-serif text-[20px] font-semibold tracking-tight">{title}</h3>
          {sub && <p className="mt-0.5 text-[13px] text-[#8A8593]">{sub}</p>}
        </div>
      </div>

      {actions.length === 0 ? (
        <div className="rounded-2xl border border-black/10 bg-white p-6 text-[13.5px] text-[#8A8593] dark:border-white/10 dark:bg-white/[0.04]">
          No actions needed right now. This {scope === 'class' ? 'class is' : 'learner is'} on track.
        </div>
      ) : (
        <div className={cn('grid grid-cols-1 gap-4', columns === 2 && 'lg:grid-cols-2')}>
          {actions.map((a) => <ActionCard key={a.id} action={a} onOpenTopic={onOpenTopic} />)}
        </div>
      )}
    </div>
  );
}

function ActionCard({ action: a, onOpenTopic }: { action: NextAction; onOpenTopic?: (topicId: string) => void }) {
  const sev = SEVERITY_META[a.severity];
  const affected = a.affected ?? [];
  const shown = affected.slice(0, 10);
  const more = affected.length - shown.length;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-white dark:border-white/10 dark:bg-white/[0.04]">
      <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: sev.color }} />
      <div className="p-5 pl-6">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <Pill color={sev.color}>{sev.label}</Pill>
          <Pill>{KIND_LABEL[a.kind]}</Pill>
          {a.stepsToMastery != null && (
            <Pill><Layers className="h-3 w-3" />{a.stepsToMastery} {a.stepsToMastery === 1 ? 'step' : 'steps'} to mastery</Pill>
          )}
        </div>

        <h4 className="font-serif text-[17px] font-semibold tracking-tight">{a.title}</h4>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#56505F] dark:text-[#B2B0C0]">{a.rationale}</p>

        <div className="mt-3 rounded-xl border border-black/[0.07] bg-black/[0.02] p-3.5 dark:border-white/[0.08] dark:bg-white/[0.03]">
          <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-[#6D28D9] dark:text-[#B69BF5]">
            <ArrowRight className="h-3 w-3" /> Recommended next step
          </div>
          <p className="text-[13.5px] leading-relaxed text-[#1B1826] dark:text-[#E9E8F0]">{a.action}</p>
        </div>

        {a.expectedImpact && (
          <p className="mt-2.5 flex items-start gap-1.5 text-[12.5px] text-[#8A8593]">
            <Flag className="mt-[2px] h-3.5 w-3.5 shrink-0" /> {a.expectedImpact}
          </p>
        )}

        {(shown.length > 0 || a.targetTopicId) && (
          <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-black/[0.07] pt-3.5 dark:border-white/[0.08]">
            {shown.map((name) => (
              <span key={name} className="rounded-md bg-black/[0.04] px-2 py-0.5 text-[11.5px] font-medium text-[#56505F] dark:bg-white/[0.06] dark:text-[#B2B0C0]">{name}</span>
            ))}
            {more > 0 && <span className="text-[11.5px] font-medium text-[#8A8593]">+{more} more</span>}
            {a.targetTopicId && onOpenTopic && (
              <button onClick={() => onOpenTopic(a.targetTopicId!)}
                className={cn('ml-auto inline-flex items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1 text-[12px] font-semibold text-[#6D28D9] transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:text-[#B69BF5] dark:hover:bg-white/[0.05]')}>
                Open {topicLabel(a.targetTopicId)} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
